// Ship's time. MLC records must be kept in the vessel's time, set by the Master —
// never the browser's timezone. Every function here takes an IANA zone name
// (config.timezone) and computes via Intl, so the machine's own clock setting
// can never leak into a legal record.
//
// Known limit (accepted scope): one timezone per vessel at a time. When the ship's
// clocks change, the Master updates the setting; the HoR record notes the change.

const pad = n => String(n).padStart(2, '0')

// Any instant -> ship-local wall time parts.
export function toShipTime(instant, tz, ) {
  const d = instant instanceof Date ? instant : new Date(instant)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz || 'UTC',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d)
  const g = {}
  for (const p of parts) g[p.type] = p.value
  return {
    date: `${g.year}-${g.month}-${g.day}`,
    hour: Number(g.hour), minute: Number(g.minute),
    hm: `${g.hour}:${g.minute}`,
  }
}

// 'YYYY-MM-DD' in ship's time. `now` is injectable for tests only.
export function shipToday(tz, now) {
  return toShipTime(now || new Date(), tz).date
}

export function shipNow(tz, now) {
  return toShipTime(now || new Date(), tz)
}

// Date-string arithmetic done in UTC so the machine's zone can't shift it.
export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + n))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

// The UTC instant at which a given ship-local wall time occurs.
// Converges via offset correction; handles DST on both sides of the change.
export function utcInstantOf(dateStr, timeStr, tz) {
  const want = Date.parse(`${dateStr}T${timeStr}:00Z`)
  let guess = want
  for (let i = 0; i < 3; i++) {
    const st = toShipTime(new Date(guess), tz)
    const got = Date.parse(`${st.date}T${pad(st.hour)}:${pad(st.minute)}:00Z`)
    if (got === want) break
    guess += want - got
  }
  return new Date(guess)
}

// The UTC instants bounding a ship day, plus its true length in hours.
// A DST spring-forward day is 23h; fall-back is 25h. MLC rest in that day
// is judged against the real length, and at minimum the record must not
// pretend every day is 24h.
export function shipDayBounds(dateStr, tz) {
  const start = utcInstantOf(dateStr, '00:00', tz)
  const end = utcInstantOf(addDays(dateStr, 1), '00:00', tz)
  return { start, end, hours: (end - start) / 3600000 }
}

// Migration-safe wrappers: if the vessel has no timezone set, fall back to the
// browser-local behaviour the app has always had. Nothing changes for any
// vessel until the Master explicitly sets a zone.
function browserToday(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
export function shipTodayOr(tz, now) {
  return tz ? shipToday(tz, now) : browserToday(now)
}
export function shipNowOr(tz, now) {
  if (tz) return shipNow(tz, now)
  const d = now || new Date()
  return { date: browserToday(d), hour: d.getHours(), minute: d.getMinutes(),
    hm: `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}` }
}

// One-time registration of the vessel zone (called by the auth provider when
// config loads). Lets module-level helpers use ship's time without threading
// config through every call. Until registered, falls back to browser-local —
// i.e. exactly the app's historical behaviour.
let _shipTz = null
export function setShipTz(tz) { _shipTz = tz || null }
export function getShipTz() { return _shipTz }
export function shipTodayAuto(now) { return shipTodayOr(_shipTz, now) }
export function shipNowAuto(now) { return shipNowOr(_shipTz, now) }

// Common IANA zones for the picker — yachting-weighted, UTC first.
export const COMMON_ZONES = [
  'UTC',
  'Europe/Monaco', 'Europe/Paris', 'Europe/Rome', 'Europe/Madrid', 'Europe/Athens',
  'Europe/Malta', 'Europe/Gibraltar', 'Europe/London', 'Europe/Istanbul',
  'Atlantic/Canary', 'Atlantic/Azores',
  'America/New_York', 'America/Nassau', 'America/Barbados', 'America/St_Thomas', 'America/Antigua',
  'America/Denver', 'America/Los_Angeles',
  'Asia/Dubai', 'Asia/Singapore', 'Asia/Hong_Kong', 'Indian/Maldives',
  'Australia/Sydney', 'Pacific/Auckland', 'Pacific/Tahiti', 'Pacific/Fiji',
]
